import { Card, Collapsible, Icon, Text } from '@pm/ui';
import { useTranslation } from 'react-i18next';
import {
  BACKEND_GROUP_ID_HEPATITIS_SEROLOGY,
  BACKEND_GROUP_ID_HIV,
  BACKEND_GROUP_ID_KIDNEY_FUNCTION,
  BACKEND_GROUP_ID_OTHER,
  BACKEND_GROUP_ID_STI_SCREENING,
  BACKEND_GROUP_ID_SYPHILIS,
  getTestSectionInfo,
} from './LabDetailPage/constants';

export const LabMeaningPage = () => {
  const { t } = useTranslation('labs', { keyPrefix: 'LabMeaningPage' });

  return (
    <div className="pt-xs pb-m space-y-xxl">
      <div className="space-y-s">
        <Text size="headingM" as="h1">
          {t('Title')}
        </Text>
        <Text size="bodyL">{t('Description1')}</Text>
        <Text size="bodyL">{t('Description2')}</Text>
      </div>
      <hr />
      <HowToCard />
      <div className="space-y-s">
        <Text size="headingXS" as="h2">
          {t('TypesOfTests')}
        </Text>
        <div className="flex flex-col gap-m">
          <HivCard />
          <StiCard />
          <SyphilisCard />
          <KidneyFunctionCard />
          <ViralHepatitisSerologyCard />
          <OtherCard />
        </div>
      </div>
    </div>
  );
};

type LabMeaningCardProps = {
  title: string;
  description?: string;
  description2?: string;
  children?: React.ReactNode;
};

const LabMeaningCard = ({
  title,
  description,
  description2,
  children,
}: LabMeaningCardProps) => (
  <Card size="large">
    <div className="space-y-l">
      <div className="space-y-s">
        <Text size="headingS" as="h2">
          {title}
        </Text>
        {description && <Text size="bodyL">{description}</Text>}
        {description2 && <Text size="bodyL">{description2}</Text>}
      </div>
      {children}
    </div>
  </Card>
);

const TestList = ({ ids }: { ids: string[] }) => {
  const { t } = useTranslation('labs');
  return (
    <div>
      {ids.map((id, index) => {
        const title = t(`TestNames.${id}.Title` as never, {
          defaultValue: '',
        }) as string;
        const description = t(`TestNames.${id}.Description` as never, {
          defaultValue: '',
        }) as string;
        return (
          <div key={id}>
            {index > 0 && <hr className="border-border-subtle my-s" />}
            <Collapsible
              trigger={
                <Text size="headingXS" as="h3">
                  {title}
                </Text>
              }
            >
              <Text size="bodyL" className="pt-xs">
                {description}
              </Text>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
};

const HowToCard = () => {
  const { t } = useTranslation('labs', { keyPrefix: 'LabMeaningPage.HowTo' });
  return (
    <LabMeaningCard title={t('Title')}>
      <div className="flex-col space-y-l">
        <div className="flex-col space-y-xxs">
          <Text
            size="bodyL"
            color="contentPositive"
            as="div"
            className="flex gap-xxs"
          >
            <Icon name="check_circle" color="contentPositive" />
            {t('Green')}
          </Text>
          <Text>{t('GreenText')}</Text>
        </div>
        <div className="flex-col space-y-xxs">
          <Text size="bodyL" color="contentSubdued">
            {t('Grey')}
          </Text>
          <Text>{t('GreyText')}</Text>
        </div>
        <div className="flex-col space-y-xxs">
          <Text
            size="bodyL"
            color="contentWarning"
            as="div"
            className="flex gap-xxs"
          >
            <Icon name="warning" color="contentWarning" />
            {t('Yellow')}
          </Text>
          <Text>{t('YellowText')}</Text>
        </div>
        <Text size="label" color="contentSubdued">
          {t('SubText')}
        </Text>
      </div>
    </LabMeaningCard>
  );
};

const HivCard = () => {
  const { t } = useTranslation('labs');
  const sectionInfo = getTestSectionInfo(BACKEND_GROUP_ID_HIV, t);
  return (
    <LabMeaningCard title={sectionInfo.title}>
      <TestList ids={['mtl_637', 'mtl_638']} />
    </LabMeaningCard>
  );
};

const StiCard = () => {
  const { t } = useTranslation('labs');
  const sectionInfo = getTestSectionInfo(BACKEND_GROUP_ID_STI_SCREENING, t);
  return (
    <LabMeaningCard title={sectionInfo.title}>
      <TestList
        ids={['mtl_44', 'mtl_46', 'mtl_16', 'mtl_43', 'mtl_45', 'mtl_8']}
      />
    </LabMeaningCard>
  );
};

const SyphilisCard = () => {
  const { t } = useTranslation('labs');
  const sectionInfo = getTestSectionInfo(BACKEND_GROUP_ID_SYPHILIS, t);
  return (
    <LabMeaningCard title={sectionInfo.title}>
      <TestList ids={['mtl_40']} />
    </LabMeaningCard>
  );
};

const KidneyFunctionCard = () => {
  const { t } = useTranslation('labs');
  const sectionInfo = getTestSectionInfo(BACKEND_GROUP_ID_KIDNEY_FUNCTION, t);
  return (
    <LabMeaningCard title={sectionInfo.title}>
      <TestList ids={['mtl_84']} />
    </LabMeaningCard>
  );
};

const ViralHepatitisSerologyCard = () => {
  const { t } = useTranslation('labs');
  const sectionInfo = getTestSectionInfo(
    BACKEND_GROUP_ID_HEPATITIS_SEROLOGY,
    t,
  );
  return (
    <LabMeaningCard title={sectionInfo.title}>
      <TestList ids={['mtl_83', 'mtl_36']} />
    </LabMeaningCard>
  );
};

const OtherCard = () => {
  const { t } = useTranslation('labs');
  const sectionInfo = getTestSectionInfo(BACKEND_GROUP_ID_OTHER, t);
  return (
    <LabMeaningCard
      title={sectionInfo.title}
      description={sectionInfo.description}
    >
      <TestList ids={['mtl_85', 'mtl_488', 'mtl_489', 'mtl_486', 'mtl_487']} />
    </LabMeaningCard>
  );
};
